import { DeliveryOption } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/parts/DeliveryOption';
import { DeliveryStockCounter } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/parts/DeliveryStockCounter';
import { deliveryMethodSelectorSX } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/styles/deliveryMethodSelector';
import { isSelectableStatus } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/utils/isSelectableStatus';
import { to12Hour } from '@/components/content/ProductDetails/parts/DeliveryMethodSelector/utils/to12Hour';
import { getEddConfig } from '@/data/config/eddConfig';
import { FULFILLMENT_METHOD } from '@/data/constants/inventory';
import { PdpHomeDeliveryOption } from '@/data/Content/PdpHomeDeliveryOption';
import { derivePickupReadyTime, PdpPickupOption } from '@/data/Content/PdpPickupOption';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { Box, Skeleton, Typography } from '@mui/material';
import { FilledStoreIcon } from 'presentation/svgImages/FilledStoreIcon';
import { StoreIcon } from 'presentation/svgImages/StoreIcon';
import { FC, useContext } from 'react';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

/** Dropship / Special Order pass the market-based home delivery option here. */
type PickupOptionData = PdpPickupOption | PdpHomeDeliveryOption;

type Props = {
	pickup: PickupOptionData;
	alignStockBottom?: boolean;
	/** Store whose closing time caps a same-day ready time (Dropship / Special Order path). */
	physicalStoreId?: string;
};

type AvailabilityLabels = ReturnType<typeof useLocalization<'HDMProductDetail'>>['Availability'];
type SelectorLabels = ReturnType<
	typeof useLocalization<'HDMProductDetail'>
>['DeliveryMethodSelector'];

/** 159073 AC5/AC6: only two reasons apply to pickup -- the product being
 * delivery-only (`not-available`) gets its own copy; every other disabled
 * status (out of stock, missing lead time, etc.) reads simply "Agotado". */
const getUnavailableMessage = (
	status: PickupOptionData['status'],
	Availability: AvailabilityLabels
): string => {
	if (status === 'not-available') return Availability.labels.pickupNotAvailable.t();
	return Availability.labels.empty.t();
};

/** AC8: "{Hoy | Mañana | dd Mmm}{fn} a partir de las {pickupReadyTime}" -- only
 * rendered for `with-date`; `available-no-date` (AC13) shows just the counter. */
const getDateWord = (pickup: PickupOptionData, labels: SelectorLabels): string | undefined => {
	if (pickup.status !== 'with-date') return undefined;
	if (pickup.descriptor === 'today') return labels.PickupDateToday.t();
	if (pickup.descriptor === 'tomorrow') return labels.PickupDateTomorrow.t();
	return pickup.formatted;
};

export const PickupOption: FC<Props> = ({ pickup, alignStockBottom, physicalStoreId }) => {
	const { isDeliverySelected, selectBox } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	>;
	const { Availability, DeliveryMethodSelector: labels } = useLocalization('HDMProductDetail');
	const { settings } = useSettings();
	const config = getEddConfig(settings.userData);

	const selected = !isDeliverySelected;
	const disabled = !isSelectableStatus(pickup.status);

	const unavailableMessage = disabled
		? getUnavailableMessage(pickup.status, Availability)
		: undefined;
	const dateWord = getDateWord(pickup, labels);
	const pickupReadyTime =
		'pickupReadyTime' in pickup
			? pickup.pickupReadyTime
			: derivePickupReadyTime(config, pickup, physicalStoreId)?.pickupReadyTime;

	if (pickup.status === 'pending') {
		return <Skeleton variant="text" sx={deliveryMethodSelectorSX.skeletonOption} />;
	}

	return (
		<DeliveryOption
			id="deliveryOptionPickup"
			label={labels.Pickup.t()}
			icon={
				selected && !disabled ? (
					<FilledStoreIcon sx={deliveryMethodSelectorSX.smallIcon(disabled)} />
				) : (
					<StoreIcon sx={deliveryMethodSelectorSX.smallIcon(disabled)} />
				)
			}
			selected={selected}
			disabled={disabled}
			onSelect={selectBox(FULFILLMENT_METHOD.PICKUP)}
		>
			{unavailableMessage ? (
				<Typography
					variant="bodyBaseline"
					color="grey.200"
					component="div"
					className="delivery-selector__unavailable-message"
					sx={deliveryMethodSelectorSX.spacing}
				>
					{unavailableMessage}
				</Typography>
			) : dateWord ? (
				<Box
					component="div"
					className="delivery-selector__date"
					sx={deliveryMethodSelectorSX.spacing}
				>
					<Typography
						variant="bodyBaseline"
						color="grey.500"
						fontWeight={OpenSansFontWeights.semibold}
					>
						{dateWord}{' '}
					</Typography>
					{pickupReadyTime ? (
						<>
							<Typography variant="bodyBaseline" color="grey.500">
								{labels.PickupReadyFrom.t()}
							</Typography>
							<Typography
								variant="bodyBaseline"
								color="grey.500"
								fontWeight={OpenSansFontWeights.semibold}
							>
								{to12Hour(pickupReadyTime)}
							</Typography>
						</>
					) : null}
				</Box>
			) : null}
			{!disabled && pickup.availableQuantity !== undefined ? (
				<DeliveryStockCounter availableQuantity={pickup.availableQuantity}
					alignBottom={alignStockBottom}
				/>
			) : null}
		</DeliveryOption>
	);
};
